import { DecisionWithId, ModelType } from '@livv/models';
import { getModelTypeFromLawLex } from '@livv/utils/helpers';
import { FC } from 'react';
import SameCase from './SameCase';
import SameCaseNotInDatabase from './SameCaseNotInDatabase';
import Timeline, { TimelineEntry } from '@components/Timeline';
import { parseDate } from '@utils/date';
import useSameCaseDecisions from '@utils/hooks/useSameCaseDecisions';

export interface DecisionNotInDatabase {
    date?: Date | null;
    title: string;
}

interface SameCaseTimelineProps {
    currentDecision: DecisionWithId;
    sameCase: string[];
}

type UseSameCaseDecisionsNotInDatabase = (decisionTitles: string[]) => DecisionNotInDatabase[];

const getEntriesNotInDatabase: UseSameCaseDecisionsNotInDatabase = (decisionTitles) =>
    decisionTitles.map((decisionTitle) => ({
        date: parseDate(decisionTitle),
        title: decisionTitle,
    }));

const SameCaseTimeline: FC<SameCaseTimelineProps> = ({ currentDecision, sameCase }) => {
    const sameCaseToFetch = sameCase.filter(
        (id) => getModelTypeFromLawLex(id) === ModelType.DECISION,
    );
    const sameCaseNotInDatabase = sameCase.filter(
        (id) => getModelTypeFromLawLex(id) !== ModelType.DECISION,
    );
    const [decisions] = useSameCaseDecisions(
        sameCaseToFetch.filter((id) => id !== currentDecision.id),
        currentDecision.id,
    );

    const clickableEntries = [currentDecision, ...decisions]
        .sort(({ id: id1 }, { id: id2 }) => id1.localeCompare(id2))
        .map((decision) => ({
            content: (
                <SameCase
                    dataTestId="timeline-same-case"
                    decision={decision}
                    isCurrentDecision={currentDecision.id === decision.id}
                />
            ),
            id: decision.id,
            timestamp: decision.performedAt.seconds,
        }));

    const entriesNotInDatabase = getEntriesNotInDatabase(sameCaseNotInDatabase);

    const notClickableEntries = entriesNotInDatabase
        .map(
            (decision) =>
                decision.date && {
                    content: <SameCaseNotInDatabase decision={decision} />,
                    id: decision.title,
                    timestamp: decision.date.getTime() / 1000,
                },
        )
        .filter((decision) => Boolean(decision));

    const timelineEntries = [...clickableEntries, ...notClickableEntries].filter(Boolean);

    return <Timeline timelineEntries={timelineEntries as TimelineEntry[]} />;
};

export default SameCaseTimeline;
